<template>
  <div>
    <Grid
      :max-results="rvs?.length ?? quantity"
      :rows="quantity <= 3 ? 1 : undefined"
      :sm-cols="2"
      :md-cols="3"
    >
      <template #default="{ getItemClass }">
        <CardRv
          v-for="(rv, index) in rvs"
          :key="rv.Id"
          :index="index"
          search-request-id="none"
          :is-loading="status === 'pending'"
          :rv="rv"
          cta="similar"
          discovery-source="SimilarRV"
          :class="getItemClass(index)"
          @visible:rv="queueViewableImpression"
        />
      </template>
    </Grid>

    <div
      v-if="showViewMore"
      class="mt-12 flex justify-end"
    >
      <TButton
        :to="viewMoreLink"
        variant="secondary"
        size="sm"
        rel="nofollow"
      >
        {{ t('actions.viewMoreRvs') }}
      </TButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ROUTE_BASE_NAME } from '~/constants'

const props = withDefaults(defineProps<{
  rvId: string
  aliasName?: string
  quantity?: number
}>(), {
  quantity: 3,
})

const { t } = useI18n()
const localePath = useLocalePath()
const { isWebView } = usePlatform()

const { $captureError } = useNuxtApp()
const route = useRoute()
const { formatAsDateISO } = useDates()

const { StartDate: start, EndDate: end } = route.query

const viewMoreLink = computed(() => localePath({
  name: ROUTE_BASE_NAME.rv_rental_similar_rvs,
  params: {
    alias: props.aliasName,
  },
  query: {
    ...(start ? { StartDate: start as string } : {}),
    ...(end ? { EndDate: end as string } : {}),
  },
}))

const { data, error, status } = await useRentals('/api/rvlistings/similar-rvs', {
  query: {
    RVId: props.rvId,
    StartDate: start ? formatAsDateISO(start as string) : null,
    EndDate: end ? formatAsDateISO(end as string) : null,
    RVCount: props.quantity,
  },
  lazy: true,
})

if (error.value) {
  $captureError(error.value)
}

const rvs = computed(() => data.value?.ListRVs ?? [])
const showViewMore = computed(() => {
  const totalRvs = data.value?.TotalRVs ?? 0
  const hasMoreRvs = totalRvs > props.quantity
  const canLinkToAlias = Boolean(props.aliasName)

  return !isWebView.value && hasMoreRvs && canLinkToAlias
})

const { queueViewableImpression } = useRvViewableImpressions({
  pageNumber: 1,
  pageSize: computed(() => props.quantity),
})
</script>
